import { css } from '@emotion/react';
import { useState, useEffect, Fragment, useRef, useCallback, forwardRef } from 'react';
import { useRecoilValue } from 'recoil';

import { helper } from '@myrealtrip/frontend-weblog';
import { logBrazeEvent } from '@myrealtrip/marketing-log';

import { useDetermineMobile } from '@myrealtrip/device-detect';
import { WeblogPageCategory, WeblogScreenName } from '../../constants/weblog';
import { Variation } from '../../types/globalSearch';

import styled from '@emotion/styled';
import useClickOutside from '../../designSystem/hooks/useClickOutside';

import SearchInput from './SearchInput';
import SearchManager from './SearchManager';
import SearchSuggestionDialog from './SearchSuggestionDialog';
import SearchSuggestionPopup from './SearchSuggestionPopup';
import { searchTerm } from './recoil';
import DomainManager from '../../utils/DomainManager';
import { useEnvContext } from '../../../modules/EnvProvider';
import { parsePlaceholder } from '../../utils/parsePlaceholder';

const { sendSearchLog, sendClickLog } = helper;

const searchManager = new SearchManager();

const baseSearchLog = {
  screenName: WeblogScreenName.COMMON,
  pageCategory: WeblogPageCategory.COMMON,
};

interface Props {
  placeholder: string;
  variations: Variation;
  isShowSearchInput: boolean;
  isSuggestionDialogOpen?: boolean;
  onSuggestionDialogToggle?: (isOpen: boolean) => void;
  className?: string;
}

function GlobalSearchContainer(
  {
    variations,
    placeholder,
    isShowSearchInput,
    isSuggestionDialogOpen,
    onSuggestionDialogToggle,
    className,
  }: Props,
  inputRef: React.ForwardedRef<HTMLInputElement>,
) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const searchKeyword = useRecoilValue(searchTerm);
  const isMobile = useDetermineMobile();
  const { targetEnv } = useEnvContext();
  const suggestionRefs = useRef<Array<HTMLAnchorElement>>([]);
  const suggestionPopupRef = useRef<HTMLDivElement>(null);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [selectedItem, setSelectedItem] = useState<HTMLAnchorElement | null>(null);

  const sendLog = (searchWord: string, targetUrl?: string) => {
    const domainManager = new DomainManager(targetEnv);

    try {
      const nextTargetUrl =
        targetUrl ||
        `${domainManager.getTravelerServiceDomain()}/search-bridge?q=${encodeURIComponent(
          searchWord,
        )}`;

      logBrazeEvent('search_offer', {
        search_offer: searchWord,
      });

      sendSearchLog({
        ...baseSearchLog,
        itemKind: 'search',
        eventName: 'gsearch',
        searchWord,
        targetUrl: nextTargetUrl,
      });
    } catch (e) {
      // ignore;
    }
  };

  const handleSuggestionPopupToggle = (isOpen: boolean) => {
    if (isMobile) {
      return;
    }

    setIsPopupOpen(isOpen);
  };

  const handleSuggestionDialogToggle = (isOpen: boolean) => {
    if (!isMobile) {
      return;
    }

    onSuggestionDialogToggle?.(isOpen);
  };

  const { ref } = useClickOutside(isPopupOpen, [], () => handleSuggestionPopupToggle(false));

  const scrollTo = (top: number) => {
    suggestionPopupRef.current?.scrollTo({
      top,
      behavior: 'smooth',
    });
  };

  const moveScroll = (to?: number) => {
    const { current: suggestionList } = suggestionRefs;

    const currentItemHeight = suggestionList[0].offsetHeight;
    const currentTop = currentItemHeight * selectedIdx;

    scrollTo(to || currentTop);
  };

  const handleSearch = () => {
    const keyword = searchKeyword || parsePlaceholder(placeholder);
    sendLog(keyword);
    searchManager.redirectSearchPage(keyword, targetEnv);
  };

  const handlePressKeyboard = (key: string) => {
    const { current: suggestionList } = suggestionRefs;
    const listLength = suggestionList.length;

    switch (key) {
      case 'Enter': {
        // 키보드 방향키로 아이템을 선택한 경우
        if (selectedItem) {
          const searchWord = selectedItem.id;
          const targetUrl = selectedItem.href;

          sendLog(searchWord, targetUrl);
          window.location.href = targetUrl;
        } else {
          handleSearch();
        }
        break;
      }
      case 'ArrowUp':
        if (selectedIdx > 0) {
          moveScroll();
          setSelectedItem(suggestionList[selectedIdx - 1]);
          setSelectedIdx((prev) => prev - 1);
        } else if (listLength > 0) {
          moveScroll(0);
          setSelectedItem(null);
          setSelectedIdx(-1);
        }
        break;
      case 'ArrowDown':
        if (listLength - 1 > selectedIdx) {
          moveScroll();
          setSelectedItem(suggestionList[selectedIdx + 1]);
          setSelectedIdx((prev) => prev + 1);
        }
        break;
      default:
        break;
    }
  };

  const handleRef = (idx: number, node: HTMLAnchorElement) => {
    if (node) {
      suggestionRefs.current[idx] = node;
      suggestionRefs.current = suggestionRefs.current.slice(0, idx + 1);
    } else {
      suggestionRefs.current = [];
    }
  };

  const handleSendClickLog = useCallback(() => {
    sendClickLog({
      ...baseSearchLog,
      itemKind: 'button',
      eventName: 'search_bar',
      itemName: 'search_bar',
    });
  }, []);

  useEffect(() => {
    setSelectedItem(null);
    setSelectedIdx(-1);
  }, [searchKeyword]);

  return (
    <Fragment>
      <Container isMobile={isMobile} isShow={isShowSearchInput} ref={ref} className={className}>
        <SearchInput
          variations={variations}
          placeholder={placeholder}
          onDialogToggle={handleSuggestionDialogToggle}
          onPopupToggle={handleSuggestionPopupToggle}
          onPressKeyboard={handlePressKeyboard}
          onClick={handleSendClickLog}
          onSearch={handleSearch}
          ref={inputRef}
        />
        {isPopupOpen && (
          <SearchSuggestionPopup
            isOpen={isPopupOpen}
            variations={variations}
            handleRef={handleRef}
            ref={suggestionPopupRef}
            selectedIdx={selectedIdx}
          />
        )}
      </Container>
      {isSuggestionDialogOpen && (
        <SearchSuggestionDialog
          isOpen={Boolean(isSuggestionDialogOpen)}
          variations={variations}
          placeholder={placeholder}
          onDialogToggle={handleSuggestionDialogToggle}
          onPressKeyboard={handlePressKeyboard}
          onSearch={handleSearch}
        />
      )}
    </Fragment>
  );
}

export default forwardRef(GlobalSearchContainer);

const Container = styled.div<{ isMobile: boolean; isShow: boolean }>`
  position: relative;
  ${({ isShow }) =>
    !isShow &&
    css`
      display: none;
    `};
  ${({ isMobile }) =>
    !isMobile &&
    css`
      margin-right: 14px;
    `};
`;
